import { useState, useEffect, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { type PageContent, type ThemeData } from '@/mocks/websiteBuilder';
import { generatePageHTML, determineInitialPageId } from '@/utils/htmlGenerator';
import type { GlobalContactInfo } from '../page';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { MODULES } from '../../../telemetry/constants/modules';
import { ACTIONS } from '../../../telemetry/constants/actions';

interface PreviewProps {
  onClose: () => void;
  theme: ThemeData;
  pages: PageContent[];
  brandName: string;
  brandLogo: string | null;
  globalContactInfo: GlobalContactInfo;
  initialPageId?: string;
}

export default function WebsitePreview({ onClose, theme, pages, brandName, brandLogo, globalContactInfo, initialPageId }: PreviewProps) {
  const { trackAction } = useTelemetry({ module: MODULES.WEBSITE_BUILDER, submodule: 'Website Preview' });
  const initialPage = useMemo(() => determineInitialPageId(pages, initialPageId), [pages, initialPageId]);
  const [currentPageId, setCurrentPageId] = useState<string>(initialPage);
  const [historyStack, setHistoryStack] = useState<string[]>([initialPage]);
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    trackAction(ACTIONS.PREVIEW_WEBSITE, 'Preview Website');
  }, [trackAction]);

  useEffect(() => {
    if (!pages.some(p => p.pageId === currentPageId)) {
      const nextId = determineInitialPageId(pages, initialPageId);
      setCurrentPageId(nextId);
      setHistoryStack([nextId]);
    }
  }, [pages, currentPageId, initialPageId]);

  useEffect(() => {
    setLoading(true);
    
    // We add a tiny delay to allow the loading spinner to render during quick transitions
    const timer = setTimeout(() => {
      try {
        let page = pages.find(p => p.pageId === currentPageId);
        if (!page) {
          page = pages.find(p => p.pageId === initialPage) || pages[0]; // fallback
        }
        
        if (page) {
          try {
            const rawHTML = generatePageHTML(page, theme, pages, brandName, brandLogo, globalContactInfo, true);
            
            // Sanitize the HTML to prevent malformed injections from breaking the iframe
            const sanitizedHTML = DOMPurify.sanitize(rawHTML, {
              ADD_TAGS: ['iframe', 'style', 'script', 'link', 'meta', 'head', 'html', 'title'],
              ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'scrolling', 'target', 'onclick', 'data-nav-id', 'onmouseover', 'onmouseout', 'href', 'rel', 'data-post'],
              WHOLE_DOCUMENT: true,
            });
            
            setHtmlContent(sanitizedHTML);
          } catch (err) {
            console.error("Preview Render Error generating page:", err);
            setHtmlContent(`<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;color:#666;">
              <h2 style="color:#ef4444;margin-bottom:8px;">Preview failed to load this page</h2>
              <p>Please refresh or regenerate the content.</p>
              <pre style="background:#f1f5f9;padding:12px;border-radius:8px;font-size:12px;margin-top:16px;max-width:80%;overflow:auto;">${err instanceof Error ? err.message : String(err)}</pre>
            </div>`);
          }
        }
      } catch (err) {
        console.error("Preview Render Error general:", err);
      } finally {
        setLoading(false);
      }
    }, 150);

    return () => clearTimeout(timer);
  }, [currentPageId, pages, theme, brandName, brandLogo, globalContactInfo, initialPage]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NAVIGATE' && event.data?.pageId) {
        let targetPageId = event.data.pageId;
        // Clean up hrefs if they were passed (e.g. index.html -> home)
        if (targetPageId.endsWith('.html')) {
           targetPageId = targetPageId.replace('.html', '');
           if (targetPageId === 'index') targetPageId = 'home';
        }
        
        const exists = pages.some(p => p.pageId === targetPageId);
        if (exists) {
          setCurrentPageId(targetPageId);
          setHistoryStack(prev => [...prev, targetPageId]);
        }
      } else if (event.data?.type === 'CLOSE_PREVIEW') {
        onClose();
      } else if (event.data?.type === 'GO_BACK') {
        setHistoryStack(prev => {
          if (prev.length > 1) {
            const next = prev.slice(0, -1);
            setCurrentPageId(next[next.length - 1]);
            return next;
          } else {
            onClose();
            return prev;
          }
        });
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [pages, onClose]);

  return (
    <div className="fixed inset-0 z-[100] bg-white overflow-hidden flex flex-col">
      {/* Browser chrome bar */}
      <div className="flex-shrink-0 bg-[#1a1a2e] px-4 py-2 flex items-center gap-3 relative z-10">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400"></div>
          <div className="w-3 h-3 rounded-full bg-amber-400"></div>
          <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
        </div>
        <div className="flex-1 bg-[#2a2a3e] rounded-md px-3 py-1 text-xs text-gray-400 flex items-center justify-center gap-2 max-w-xl mx-auto">
          <i className="ri-lock-line text-[10px]"></i>
          <span>yourbrand.com/{currentPageId === 'home' ? '' : currentPageId}</span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white text-sm flex items-center gap-1.5 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-close-line"></i>
          Close Preview
        </button>
      </div>

      {/* Iframe Content */}
      <div className="flex-1 relative bg-gray-100">
        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/80 backdrop-blur-sm transition-opacity duration-300">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 border-4 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-500 font-medium">Loading preview...</p>
            </div>
          </div>
        )}
        <iframe
          srcDoc={htmlContent}
          className="w-full h-full border-0"
          title="Website Preview"
          sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        />
      </div>
    </div>
  );
}